import { useState } from 'react';
import { useIsMobile } from 'src/hooks';
import { TextField, Button, Stack, Typography, Paper, SendIcon, CloseIcon } from 'src/components';
import { createComment, updateComment } from 'src/utils';

export default function CommentForm({
  articleId,
  parentCommentId = null,
  commentId = null,
  initialText = '',
  onSuccess,
  onCancel,
  autoFocus = false
}) {
  const [text, setText] = useState(initialText);
  const [loading, setLoading] = useState(false);

  const isMobile = useIsMobile();

  const isEditing = !!commentId;
  const isReply = !!parentCommentId;

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setLoading(true);

    try {
      if (commentId) {
        await updateComment({ text });
        onSuccess?.({ id: commentId, text });
      } else {
        // New comment or reply
        const res = await createComment({
          articleId,
          parentCommentId,
          text
        });
        onSuccess?.(res);
      }

      setText('');
    } catch (err) {
      console.error('Error submitting comment', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        backgroundColor: '#fff',
        borderColor: '#fff',
        transition: 'all 0.2s ease-in-out',
        borderRadius: 5
      }}
    >
      {!isEditing && !isReply && (
        <Typography 
          variant="subtitle2" 
          mb={1.5}
          fontWeight={600}
          color="text.primary"
          sx={{ fontSize: isMobile ? 14 : 13 }}
        >
          Share your thoughts
        </Typography>
      )}

      <TextField
        fullWidth
        multiline
        autoFocus={autoFocus}
        variant="outlined"
        minRows={isReply ? 2 : 1}
        maxRows={8}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={
          isEditing 
            ? 'Edit your comment...' 
            : isReply 
              ? 'Write a reply...' 
              : 'What are your thoughts?'
        }
        disabled={loading}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: isMobile ? 16 : 14,
            backgroundColor: 'background.paper',
            '& fieldset': {
              borderColor: 'divider',
            },
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiOutlinedInput-input': {
            lineHeight: 1.4,
          },
          '& .MuiInputBase-inputMultiline': {
            resize: 'vertical',
          },
          '& textarea': {
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#ccc',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#999',
            },
            scrollbarWidth: 'thin', // Firefox
            scrollbarColor: '#ccc transparent',
          }
        }}
      />

      <Stack 
        direction="row" 
        spacing={1} 
        mt={1.5}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={!text.trim() || loading}
            startIcon={<SendIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 0.75,
              fontSize: isMobile ? 13 : 12,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 1,
              }
            }}
          >
            {loading ? 'Posting...' : isEditing ? 'Save Changes' : 'Post Comment'}
          </Button>

          {onCancel && (
            <Button
              variant="text"
              size="small"
              onClick={onCancel}
              disabled={loading}
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 0.75,
                fontSize: isMobile ? 13 : 12,
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'text.primary'
                }
              }}
            >
              Cancel
            </Button>
          )}
        </Stack>

      </Stack>
    </Paper>
  );
}